import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, computed, effect, ElementRef, inject, input, viewChild, viewChildren } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';

import { debounceTime, map } from 'rxjs';

import { LangDefinition, TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ChrComponentsModule, ChrNavGroupDirective } from '@ciphr-design-system/angular';

import { AppBrandingService } from '@ciphr/core/app-branding';
import { AvatarDirective } from '@ciphr/shared/avatar';
import { NoResultComponent } from '@ciphr/ui';

import { AUTH_PANEL_SERVICE } from './auth-panel/auth-panel-service.token';
import { languagesIconsMap } from './maps/languages-icons.map';
import { NavigationItemTypes } from '../models/navigation-item-types.enum';
import { NavigationLinkDirective } from './navigation-link.directive';
import { navigationPalettesMap } from './maps/navigation-palettes.map';
import { NavigationProductItem } from '../models/navigation-product-item.type';
import { NavigationStore } from '../state/navigation.store';
import { productsIconsMap } from './maps/products-icons.map';

@Component({
  selector: 'ciphr-navigation',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslocoDirective,
    ChrComponentsModule,
    AvatarDirective,
    NavigationLinkDirective,
    KeyValuePipe,
    NoResultComponent,
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  host: { '[class.hidden]': 'hidden()' },
})
export class NavigationComponent {
  private readonly appBrandingService = inject(AppBrandingService);
  private readonly authPanelService = inject(AUTH_PANEL_SERVICE);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly navigationStore = inject(NavigationStore);
  private readonly translocoService = inject(TranslocoService);

  desktopVersionUrl = input<string | null>(null);

  private readonly navGroups = viewChildren(ChrNavGroupDirective);
  private readonly searchNavGroup = viewChild.required('searchNavGroup', { read: ChrNavGroupDirective });
  private readonly closeButton = viewChild('closeButton', { read: ElementRef });

  private readonly activeLanguageId = toSignal(this.translocoService.langChanges$);

  readonly searchControl = new FormControl('', { nonNullable: true });

  readonly activeLanguageLabel = computed(() => this.languages.find((language) => language.id === this.activeLanguageId())?.label);
  readonly authIssuer = this.authPanelService.issuer;
  readonly handset = toSignal(this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((state) => state.matches)), {
    requireSync: true,
  });
  readonly hidden = computed(() => this.handset() && this.navigationStore.hiddenOnHandset());
  readonly mobileMode = toSignal(
    this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.Tablet]).pipe(map((state) => state.matches)),
    {
      initialValue: this.breakpointObserver.isMatched([Breakpoints.Handset, Breakpoints.Tablet]),
    },
  );
  readonly isSsoAdmin = this.authPanelService.isSsoAdmin;
  readonly languages = this.translocoService.getAvailableLangs() as LangDefinition[];
  readonly logo = this.appBrandingService.brandingLogo;
  readonly navigationItemTypes = NavigationItemTypes;
  readonly palette = computed(() => navigationPalettesMap.get(this.appBrandingService.navigationBrandingLevel()) || 'neutral');
  readonly positionsMap = this.navigationStore.positionsMap;
  readonly products = computed(() => this.navigationStore.products().filter((product) => product.isActive));
  readonly unPurchasedProducts = computed(() => this.navigationStore.products().filter((product) => !product.isActive));
  readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(debounceTime(400)), { initialValue: this.searchControl.value });
  readonly searchResults = computed(() => (this.searchQuery() ? this.navigationStore.search(this.searchQuery()) : null));
  readonly username = this.authPanelService.username;

  private wasHiddenOnHandset = true;

  constructor() {
    effect(() => {
      const hidden = this.hidden();
      const opening = !hidden && this.wasHiddenOnHandset;

      this.wasHiddenOnHandset = hidden;

      if (!opening || !this.handset()) {
        return;
      }

      this.closeButton()?.nativeElement.focus();
    });

    effect(() => {
      let retries = 0;
      const maxRetries = 50;
      const interval = setInterval(() => {
        // Need to make sure one nav group is selected
        const selectedNavGroup = this.navGroups().find((group) => group.selected);
        if (selectedNavGroup) {
          selectedNavGroup.active = true;
          clearInterval(interval);
        } else if (++retries >= maxRetries) {
          clearInterval(interval);
        }
      }, 100);
    });
  }

  skipToMain(): void {
    const main = document.querySelector('main');
    if (!main) return;

    const el = main as HTMLElement;

    // Make sure it can be focused
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '-1');
    }

    // Move focus + scroll to it
    el.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Preserve the original order of results.
   * W/o it KeyValuePipe sorts results alphabetically.
   */
  compareSearchResults(): number {
    return 0;
  }

  getLanguageIcon(languageId: string): string {
    return languagesIconsMap.get(languageId) || '';
  }

  getProductIcon(application: NavigationProductItem['application']): string {
    return productsIconsMap.get(application) ?? 'question';
  }

  logOut(): void {
    this.authPanelService.logOut();
  }

  selectActiveLanguage(languageId: string): void {
    this.translocoService.setActiveLang(languageId);
  }

  switchToActiveNavGroup(): void {
    this.searchNavGroup().deactivate();
    this.navGroups()
      .find((group) => group.selected)
      ?.activate();
  }

  toggleVisibility(): void {
    this.navigationStore.toggleVisibility();
  }

  getUnpucharsedDisplayLength(): number {
    const height = window.innerHeight;
    const productsLength = this.products().length;

    let extra = 6;
    if (height <= 700) extra = 1;
    else if (height <= 800) extra = 2;
    else if (height <= 900) extra = 3;

    const activeProductsSubtract = productsLength >= 3 ? productsLength - 3 : 0;

    return extra - activeProductsSubtract;
  }

  getUnpucharsedItems(): NavigationProductItem[] {
    const unparsedItemsLength = this.getUnpucharsedDisplayLength();

    if (unparsedItemsLength < 0) {
      return [];
    }

    return this.unPurchasedProducts().slice(0, unparsedItemsLength);
  }
}
